import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import { FileUp, Download, History, AlertCircle, CheckCircle, Clock, X, FileText, FileSpreadsheet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient, getApiBaseUrl, joinUrl } from "@/lib/queryClient";

type UploadType = 'employee' | 'candidate';
type FileFormat = 'csv' | 'xlsx';

interface UploadHistory {
  id: number;
  originalFileName: string;
  fileType: FileFormat;
  uploadType: UploadType;
  totalRows: number;
  successfulRows: number;
  errorRows: number;
  status: 'processing' | 'completed' | 'failed';
  uploadedAt: string;
  completedAt?: string;
  fileSize: number;
  uploadedBy: string;
  uploadedByLastName: string;
}

export default function BulkUpload() {
  const { toast } = useToast();
  const [selectedType, setSelectedType] = useState<UploadType>('employee');
  const [selectedFormat, setSelectedFormat] = useState<FileFormat>('xlsx');
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);

  // Fetch upload history
  const { data: historyData, isLoading: isLoadingHistory, refetch: refetchHistory } = useQuery({
    queryKey: ['/admin/bulk-upload/history'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/bulk-upload/history");
      return response.json();
    },
    refetchOnWindowFocus: false,
  });

  // Poll for processing uploads
  useEffect(() => {
    const hasProcessing = historyData?.uploads?.some(upload => upload.status === 'processing');
    
    if (hasProcessing) {
      const interval = setInterval(() => {
        refetchHistory();
      }, 3000); // Poll every 3 seconds
      
      return () => clearInterval(interval);
    }
  }, [historyData, refetchHistory]);

  // Download template mutation
  const downloadTemplateMutation = useMutation({
    mutationFn: async ({ type, format }: { type: UploadType; format: FileFormat }) => {
      const response = await apiRequest("GET", `/admin/bulk-upload/template?type=${type}&format=${format}`);
      return response;
    },
    onSuccess: async (response, variables) => {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${variables.type}_template.${variables.format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Template Downloaded",
        description: `${variables.type} template in ${variables.format.toUpperCase()} format downloaded successfully.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Download Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Upload file mutation
  const uploadFileMutation = useMutation({
    mutationFn: async ({ file, type }: { file: File; type: UploadType }) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);
      
      const baseUrl = getApiBaseUrl();
      const uploadUrl = baseUrl ? joinUrl(baseUrl, '/admin/bulk-upload') : '/admin/bulk-upload';
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Upload Started",
        description: "File uploaded successfully. Processing in background...",
      });
      setUploadingFiles([]);
      // Invalidate and refetch history to show the new upload
      queryClient.invalidateQueries({ queryKey: ['/admin/bulk-upload/history'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Upload Failed",
        description: error.message,
        variant: "destructive",
      });
      setUploadingFiles([]);
    },
  });

  // Download error report mutation
  const downloadErrorsMutation = useMutation({
    mutationFn: async ({ uploadId, format }: { uploadId: number; format: FileFormat }) => {
      const response = await apiRequest("GET", `/admin/bulk-upload/${uploadId}/errors?format=${format}`);
      return { response, uploadId, format };
    },
    onSuccess: async ({ response, uploadId, format }) => {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `upload_${uploadId}_errors.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Error Report Downloaded",
        description: `Error report downloaded successfully.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Download Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter(file => {
      const extension = file.name.toLowerCase().split('.').pop();
      return ['csv', 'xlsx', 'xls'].includes(extension || '');
    });

    if (validFiles.length === 0) {
      toast({
        title: "Invalid File Format",
        description: "Please upload only CSV or Excel files.",
        variant: "destructive",
      });
      return;
    }

    if (validFiles.length > 1) {
      toast({
        title: "Multiple Files",
        description: "Please upload one file at a time.",
        variant: "destructive",
      });
      return;
    }

    setUploadingFiles(validFiles);
  }, [toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
  });

  const handleUpload = () => {
    if (uploadingFiles.length === 0) return;
    
    uploadFileMutation.mutate({
      file: uploadingFiles[0],
      type: selectedType,
    });
  };

  const handleDownloadTemplate = () => {
    downloadTemplateMutation.mutate({
      type: selectedType,
      format: selectedFormat,
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-servify-medium-grey" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      processing: "bg-yellow-100 text-yellow-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800",
    };
    
    return (
      <Badge className={colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-servify-white-grey py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-servify-dark-grey mb-2">Bulk Upload</h1>
          <p className="text-servify-medium-grey">Upload multiple employees or candidates using CSV or Excel files</p>
        </div>

        <Tabs defaultValue="upload" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <FileUp className="h-4 w-4" />
              Upload Files
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Upload History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="space-y-8">
            {/* Template Download Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  Download Template
                </CardTitle>
                <p className="text-sm text-servify-medium-grey">
                  Download a template file with the correct format and required fields.
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pb-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-servify-dark-grey mb-2 block">Type</label>
                    <Select value={selectedType} onValueChange={(value: UploadType) => setSelectedType(value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent 
                        position="popper" 
                        side="bottom" 
                        align="start"
                        className="z-[100] bg-white border shadow-lg min-w-[160px]"
                        sideOffset={4}
                      >
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="candidate">Candidate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-servify-dark-grey mb-2 block">Format</label>
                    <Select value={selectedFormat} onValueChange={(value: FileFormat) => setSelectedFormat(value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent 
                        position="popper" 
                        side="bottom" 
                        align="start"
                        className="z-[100] bg-white border shadow-lg min-w-[160px]"
                        sideOffset={4}
                      >
                        <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                        <SelectItem value="csv">CSV (.csv)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleDownloadTemplate}
                      disabled={downloadTemplateMutation.isPending}
                      className="w-full"
                    >
                      {downloadTemplateMutation.isPending ? (
                        <>
                          <Clock className="h-4 w-4 mr-2 animate-spin" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Download Template
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* File Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileUp className="h-5 w-5" />
                  Upload File
                </CardTitle>
                <p className="text-sm text-servify-medium-grey">
                  Upload a CSV or Excel file with user data. Make sure to use the template format.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-sm font-medium text-servify-dark-grey mb-2 block">Upload Type</label>
                    <Select value={selectedType} onValueChange={(value: UploadType) => setSelectedType(value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent 
                        position="popper" 
                        side="bottom" 
                        align="start"
                        className="z-[100] bg-white border shadow-lg min-w-[160px]"
                        sideOffset={4}
                      >
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="candidate">Candidate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* File Upload Button */}
                <div className="text-center space-y-4">
                  <Button
                    onClick={() => document.getElementById('file-input')?.click()}
                    variant="outline"
                    className="w-full h-16 text-lg border-2 border-dashed border-servify-light-grey hover:border-servify-purple hover:bg-servify-purple/5"
                  >
                    <FileUp className="h-6 w-6 mr-3" />
                    Choose File to Upload
                  </Button>
                  <input
                    id="file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files) {
                        onDrop(Array.from(files));
                      }
                    }}
                    className="hidden"
                  />
                  <p className="text-sm text-servify-medium-grey">
                    Supports CSV and Excel files (max 10MB)
                  </p>
                </div>

                {/* Selected Files */}
                {uploadingFiles.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-servify-dark-grey">Selected File:</h4>
                    {uploadingFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-servify-white-grey rounded-lg">
                        <div className="flex items-center gap-3">
                          {file.name.endsWith('.csv') ? (
                            <FileText className="h-5 w-5 text-green-600" />
                          ) : (
                            <FileSpreadsheet className="h-5 w-5 text-green-600" />
                          )}
                          <div>
                            <p className="text-sm font-medium text-servify-dark-grey">{file.name}</p>
                            <p className="text-xs text-servify-medium-grey">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setUploadingFiles([])}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button 
                      onClick={handleUpload}
                      disabled={uploadFileMutation.isPending}
                      className="w-full bg-servify-purple hover:bg-servify-purple/90 text-white"
                    >
                      {uploadFileMutation.isPending ? (
                        <>
                          <Clock className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FileUp className="h-4 w-4 mr-2" />
                          Upload & Process
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Upload History
                </CardTitle>
                <p className="text-sm text-servify-medium-grey">
                  View and download reports from previous bulk uploads
                </p>
              </CardHeader>
              <CardContent>
                {isLoadingHistory ? (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 text-servify-medium-grey mx-auto mb-2 animate-spin" />
                    <p className="text-servify-medium-grey">Loading upload history...</p>
                  </div>
                ) : historyData?.uploads?.length === 0 ? (
                  <div className="text-center py-8">
                    <FileUp className="h-8 w-8 text-servify-medium-grey mx-auto mb-2" />
                    <p className="text-servify-medium-grey">No uploads found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>File Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Rows</TableHead>
                          <TableHead>Success/Errors</TableHead>
                          <TableHead>Uploaded By</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historyData?.uploads?.map((upload: UploadHistory) => (
                          <TableRow key={upload.id}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {upload.fileType === 'csv' ? (
                                  <FileText className="h-4 w-4 text-green-600" />
                                ) : (
                                  <FileSpreadsheet className="h-4 w-4 text-green-600" />
                                )}
                                {upload.originalFileName}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {upload.uploadType}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getStatusIcon(upload.status)}
                                {getStatusBadge(upload.status)}
                              </div>
                            </TableCell>
                            <TableCell>{upload.totalRows}</TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div className="text-green-600">✓ {upload.successfulRows} success</div>
                                {upload.errorRows > 0 && (
                                  <div className="text-red-600">✗ {upload.errorRows} errors</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {upload.uploadedBy} {upload.uploadedByLastName}
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                <div>{formatDate(upload.uploadedAt)}</div>
                                {upload.completedAt && (
                                  <div className="text-servify-medium-grey">
                                    Completed: {formatDate(upload.completedAt)}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {upload.errorRows > 0 && (
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadErrorsMutation.mutate({ 
                                      uploadId: upload.id, 
                                      format: 'csv' 
                                    })}
                                    disabled={downloadErrorsMutation.isPending}
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    CSV
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadErrorsMutation.mutate({ 
                                      uploadId: upload.id, 
                                      format: 'xlsx' 
                                    })}
                                    disabled={downloadErrorsMutation.isPending}
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    Excel
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}